declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const STORAGE_KEY = 'unicode-tools-analytics-consent';

export type AnalyticsConsent = 'granted' | 'denied';

export function getStoredAnalyticsConsent(): AnalyticsConsent | null {
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'granted' || v === 'denied') {
    return v;
  }
  return null;
}

export function setStoredAnalyticsConsent(decision: AnalyticsConsent): void {
  localStorage.setItem(STORAGE_KEY, decision);
}

/** Filled from /runtime-config.json when VITE_GA_MEASUREMENT_ID was not set at build time (e.g. Amplify). */
let runtimeGaMeasurementId: string | undefined;

export function setRuntimeGaMeasurementId(id: string | undefined): void {
  runtimeGaMeasurementId = id?.trim() || undefined;
}

/**
 * GA4 Measurement ID: build-time `VITE_GA_MEASUREMENT_ID`, else runtime `public/runtime-config.json` (see Amplify build step).
 */
export function getGaMeasurementId(): string | undefined {
  const fromEnv = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (fromEnv != null && String(fromEnv).trim() !== '') {
    return String(fromEnv).trim();
  }
  if (runtimeGaMeasurementId != null && runtimeGaMeasurementId !== '') {
    return runtimeGaMeasurementId;
  }
  return undefined;
}

let bootstrapStarted = false;

/** True only after `gtag('config', measurementId, …)` runs — required before consent updates / events. */
let gtagConfigComplete = false;

/** Consent Mode v2 defaults (denied) until user accepts. */
function pushConsentDefaultDenied(): void {
  window.gtag?.('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
}

/** Grant analytics storage only (no ads). */
export function pushConsentUpdateGranted(): void {
  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
  });
}

/** Re-deny all consent signals (e.g. user declines or revokes). */
export function pushConsentUpdateDenied(): void {
  window.gtag?.('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

/**
 * Initializes dataLayer + gtag stub, Consent Mode v2 default denied, loads gtag.js, then GA4 config.
 * Uses send_page_view: false so no hit is sent until analytics_storage is granted; then SPA uses event page_view.
 */
export function bootstrapGoogleAnalyticsWithConsentMode(measurementId: string): void {
  if (bootstrapStarted || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  bootstrapStarted = true;
  gtagConfigComplete = false;

  // Match Google's inline snippet: `function gtag(){dataLayer.push(arguments);}` (not `push([...args])`).
  const dataLayer = (window.dataLayer = window.dataLayer ?? []) as unknown[];
  window.gtag = function gtag() {
    dataLayer.push(arguments);
  };

  pushConsentDefaultDenied();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.onload = () => {
    window.gtag!('js', new Date());
    const debugMode =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('ga_debug') === '1';
    window.gtag!('config', measurementId, {
      send_page_view: false,
      ...(debugMode ? { debug_mode: true } : {}),
    });
    gtagConfigComplete = true;
    if (getStoredAnalyticsConsent() === 'granted') {
      pushConsentUpdateGranted();
      sendGtagPageViewInternal();
    }
  };
  document.head.appendChild(script);
}

/** Persist and signal GA4 consent granted (Consent Mode v2 update) + first page_view for this session. */
export function userGrantedAnalyticsConsent(): void {
  setStoredAnalyticsConsent('granted');
  // If the user taps Accept before gtag.js + `config` run, queue only in localStorage; `script.onload` flushes.
  if (gtagConfigComplete) {
    pushConsentUpdateGranted();
    sendGtagPageViewInternal();
  }
}

/** Persist and signal GA4 consent denied (Consent Mode v2 update). */
export function userDeniedAnalyticsConsent(): void {
  setStoredAnalyticsConsent('denied');
  if (gtagConfigComplete) {
    pushConsentUpdateDenied();
  }
}

function sendGtagPageViewInternal(path?: string, pageTitle?: string): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }
  if (getStoredAnalyticsConsent() !== 'granted') {
    return;
  }
  const pagePath = path ?? `${window.location.pathname}${window.location.search}`;
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle ?? document.title,
    page_location: window.location.href,
  });
}

/**
 * SPA navigation: GA4-recommended `event` page_view (not config) after Consent Mode grant.
 */
export function sendGtagPageView(pagePath: string, pageTitle?: string): void {
  sendGtagPageViewInternal(pagePath, pageTitle);
}
